/**
 * A comment that names nobody goes to the session you are working with
 * (RFC 0301 §3d, S10).
 *
 * `offerComment` is called where a root comment is written, beside
 * `deliverMentions`, and does the one thing that call cannot: reach a session
 * nobody named. WHICH session is the caller's to say, because only the window
 * knows: the space's current session, the one the compact composer shows. The
 * core cannot see a space, so it takes the id and does the delivery.
 *
 * THIS REPLACED THE CLAIM. S5 offered an unaddressed comment to every session
 * paired to the document and let the first claim it. On a review page three
 * sessions had been paired over two days, so a comment made in one space was
 * answered by a session in another, and the reply appeared in no thread the
 * person was reading. A person can see the space and cannot see the pairings,
 * so the space decides, and with one candidate there is no race to settle.
 *
 * DELIVERY IS A FIRING, not a bare notification, so the gate and the cost
 * fold (§3b, §3f) see it. That needs a pairing, and the session is paired here
 * if it was not already: a comment made beside a session is the same act as
 * opening the page beside it (§3a, `pairOnOpen`).
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { BranchLog } from '../../log/branch-log'
import { projectComments } from '../../model/comments/reads'
import { locateComment } from '../../model/comments/store'
import type { CommentEntry } from '../../model/comments/types'
import { projectAuthor } from '../../model/people/reads'
import type { ProjectDoc } from '../../model/project/types'
import { gateSignal } from '../../model/session/pairing-gate'
import { activePairing, pairSession } from '../../model/session/pairings'
import { projectSessionFirings } from '../../model/session/reads'
import { enqueueFiring } from '../run/enqueue-run'

const SUMMARY_MAX = 280

export interface OfferCommentResult {
  /** The session answering, or null when nobody is. */
  answeredBy: string | null
  reason?:
    | 'addressed'
    | 'reply'
    | 'no-document'
    | 'no-session'
    | 'own-comment'
    | 'unknown-comment'
}

async function project(repo: Repo, projectId: string) {
  const handle = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  return handle
}

/** The comment, read where it lives (RFC 0339): its comments document, or
 *  the project's leftovers. */
async function findComment(
  repo: Repo,
  projectId: string,
  commentId: string,
): Promise<CommentEntry | null> {
  const home = await locateComment(repo, projectId, commentId)
  if (!home) return null
  return (
    projectComments(home.handle.doc()).find((c) => c.id === commentId) ?? null
  )
}

export async function offerComment(
  repo: Repo,
  projectId: string,
  opts: {
    commentId: string
    /** The space's current session: the one the composer shows. Null when
     *  the space has none, which reaches nobody and says so. */
    sessionId: string | null
    /** The session that wrote it, when an agent did. It is never offered its
     *  own comment (§3g: a session never fires on its own write). */
    authorSessionId?: string
  },
): Promise<OfferCommentResult> {
  const handle = await project(repo, projectId)
  const comment = await findComment(repo, projectId, opts.commentId)
  if (!comment) return { answeredBy: null, reason: 'unknown-comment' }
  if (comment.parentId) return { answeredBy: null, reason: 'reply' }
  if (comment.mentions?.some((m) => m.kind === 'session')) {
    return { answeredBy: null, reason: 'addressed' }
  }
  const documentId = comment.target.documentId
  if (!documentId) return { answeredBy: null, reason: 'no-document' }
  const sessionId = opts.sessionId
  if (!sessionId) return { answeredBy: null, reason: 'no-session' }
  if (sessionId === opts.authorSessionId) {
    return { answeredBy: null, reason: 'own-comment' }
  }

  await pairSession(repo, projectId, {
    sessionId,
    documentId,
    createdBy: 'user',
  })
  const pairing = await activePairing(repo, projectId, sessionId, documentId)
  if (!pairing) return { answeredBy: null, reason: 'no-session' }

  const session = await repo.find(sessionId as AnyDocumentId)
  await (session as { whenReady?: () => Promise<void> }).whenReady?.()
  const append = (log: BranchLog) =>
    log.appendCommentClaimed({
      commentId: comment.id,
      documentId,
      // The writer's NAME, not their id: this is the line an agent reads.
      author: projectAuthor(handle.doc(), comment).label,
      ...(comment.target.anchorText
        ? { anchorText: comment.target.anchorText }
        : {}),
      ...(comment.target.nodeId ? { nodeId: comment.target.nodeId } : {}),
      summary: summarise(comment.body),
    })
  const verdict = gateSignal(
    'comment',
    pairing,
    projectSessionFirings(session.doc()),
  )
  if (verdict.pass) {
    await enqueueFiring(
      repo,
      sessionId,
      { pairingId: pairing.id, documentId, signal: 'comment' },
      append,
      { projectId },
    )
  } else {
    // Behind a queued turn, which reads it. Dropped for any other reason (a
    // disabled pairing) it is still this session's to answer, so it is
    // recorded rather than lost; the next turn sees it.
    append(new BranchLog(session as never))
  }
  return { answeredBy: sessionId }
}

function summarise(body: string): string {
  const line = body.trim().split('\n', 1)[0] ?? ''
  return line.length > SUMMARY_MAX ? `${line.slice(0, SUMMARY_MAX - 1)}…` : line
}
